import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "catalyst-ui-kit/badge";
import { Checkbox } from "catalyst-ui-kit/checkbox";
import Currency from "components/format/currency/currency";
import PrettyDate from "components/format/PrettyDate";
import { formatOrderReferenceLabel } from "utils/invoice-number";
import type { InvoiceableOrder } from "services/invoices.service";
import InvoiceableOrderLines, {
  lineProblem,
  type EditedLine,
} from "./invoiceable-order-lines";

type Props = {
  order: InvoiceableOrder;
  /** The order's lines including any edits the user has made. */
  lines: EditedLine[];
  selected: boolean;
  disabled?: boolean;
  edited?: boolean;
  onToggle: (orderId: string) => void;
  onLineChange: (orderId: string, externalCode: string, patch: Partial<EditedLine>) => void;
};

/**
 * One selectable order in the "Create invoice" modal.
 *
 * The products are collapsed by default and expand in place: with several
 * orders of similar dates and totals, the lines are what tells them apart, but
 * showing every line for every order at once would bury the selection itself.
 * Expanded, they can be edited before anything is issued. A row with a line
 * that cannot be invoiced as it stands stays open, so the problem is never
 * hidden behind the collapse.
 */
export default function InvoiceableOrderRow({
  order,
  lines,
  selected,
  disabled,
  edited,
  onToggle,
  onLineChange,
}: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const kept = lines.filter((line) => !line.removed);
  const hasProblem = lines.some((line) => lineProblem(line));
  const open = expanded || hasProblem;
  // The row total follows the edits, so the figure the user sees is the one
  // that will actually be invoiced.
  const amount = kept.reduce(
    (sum, line) => sum + (Number(line.quantity) || 0) * (Number(line.price) || 0),
    0
  );
  const Chevron = open ? ChevronDown : ChevronRight;
  const label =
    formatOrderReferenceLabel({ orderNumber: order.number, orderCode: order._number }) ||
    "—";

  return (
    <div
      className={`rounded-lg border px-3.5 py-3 transition-colors ${
        selected
          ? "border-indigo-300 bg-indigo-50/60 dark:border-indigo-500/40 dark:bg-indigo-500/10"
          : "border-zinc-200 dark:border-zinc-700"
      }`}
    >
      <div className="flex items-center gap-3">
        <Checkbox
          color="indigo"
          checked={selected}
          disabled={disabled}
          onChange={() => onToggle(order.id)}
          aria-label={t("select_order_for_invoice", {
            defaultValue: "Select this order for invoicing",
          })}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {t("order", { defaultValue: "Order" })} {label}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
            {order.date ? <PrettyDate dateString={order.date} /> : null}
            {/* The disclosure doubles as the line count, so the row does not
                spend space on a separate "N lines" label and a toggle. */}
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={open}
              className="inline-flex items-center gap-1 font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              <Chevron className="size-3.5" aria-hidden="true" />
              {t("invoice_order_line_count", {
                defaultValue: "{{count}} products",
                count: kept.length,
              })}
            </button>
            {hasProblem ? (
              <Badge color="red">
                {t("invoice_lines_need_fixing", { defaultValue: "Needs fixing" })}
              </Badge>
            ) : edited ? (
              <Badge color="amber">
                {t("invoice_lines_edited", { defaultValue: "Edited" })}
              </Badge>
            ) : null}
          </div>
        </div>
        <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
          <Currency value={amount} isSymbol />
        </span>
        <Badge color="green">{t("signed", { defaultValue: "Signed" })}</Badge>
      </div>

      {open && (
        <InvoiceableOrderLines
          lines={lines}
          disabled={disabled}
          onChange={(externalCode, patch) => onLineChange(order.id, externalCode, patch)}
        />
      )}
    </div>
  );
}
